<template>
  <div class="departments-view">
    <!-- Loading State -->
    <div
      v-if="isLoadingDepartment && !currentDepartment"
      class="text-center py-4"
    >
      <b-spinner
        variant="primary"
        large
      />
      <p class="mt-2">
        جارٍ تحميل تفاصيل القسم...
      </p>
    </div>

    <!-- Error State -->
    <b-alert
      v-else-if="error && !currentDepartment"
      variant="danger"
      show
    >
      <h4 class="alert-heading">
        خطأ في تحميل القسم
      </h4>
      <div class="alert-body">
        {{ error }}
      </div>
      <hr>
      <b-button
        variant="outline-danger"
        @click="$router.push('/departments/list')"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          class="mr-50"
        />
        العودة إلى القائمة
      </b-button>
    </b-alert>

    <!-- Department Details -->
    <template v-else-if="currentDepartment">
      <!-- Header -->
      <b-card no-body>
        <b-card-body>
          <b-row>
            <b-col
              cols="12"
              md="8"
            >
              <!-- Breadcrumb -->
              <div
                v-if="departmentAncestors.length > 0"
                class="mb-1"
              >
                <b-breadcrumb class="breadcrumb-slash">
                  <b-breadcrumb-item
                    v-for="ancestor in departmentAncestors"
                    :key="ancestor.id"
                    :to="{ name: 'departments-view', params: { id: ancestor.id } }"
                  >
                    {{ ancestor.name }}
                  </b-breadcrumb-item>
                  <b-breadcrumb-item active>
                    {{ currentDepartment.name }}
                  </b-breadcrumb-item>
                </b-breadcrumb>
              </div>

              <!-- Title -->
              <h2 class="mb-50">
                {{ currentDepartment.name }} ddddd
                <b-badge
                  :variant="currentDepartment.is_active ? 'light-success' : 'light-secondary'"
                  class="ml-50"
                >
                  {{ currentDepartment.is_active ? 'Active' : 'Inactive' }}
                </b-badge>
              </h2>
              <p class="text-muted">
                <b-badge
                  variant="light-secondary"
                  class="text-monospace mr-50"
                >
                  {{ currentDepartment.code }}
                </b-badge>
                <span v-if="currentDepartment.full_path">
                  {{ currentDepartment.full_path }}
                </span>
              </p>
              <p
                v-if="currentDepartment.description"
                class="mt-1"
              >
                {{ currentDepartment.description }}
              </p>
            </b-col>

            <b-col
              cols="12"
              md="4"
              class="text-right"
            >
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-primary"
                size="sm"
                class="mr-1"
                @click="editDepartment"
              >
                <feather-icon
                  icon="EditIcon"
                  class="mr-50"
                />
                تعديل
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-secondary"
                size="sm"
                @click="$router.push('/departments/list')"
              >
                <feather-icon
                  icon="ArrowLeftIcon"
                  class="mr-50"
                />
                العودة
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Statistics -->
      <b-row class="match-height mt-2">
        <b-col
          cols="12"
          md="3"
        >
          <statistic-card-horizontal
            icon="UsersIcon"
            :statistic="stats && stats.counts ? stats.counts.total_employees : 0"
            statistic-title="إجمالي الموظفين"
            color="primary"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <statistic-card-horizontal
            icon="UserCheckIcon"
            :statistic="stats && stats.counts ? stats.counts.active_employees : 0"
            statistic-title="الموظفون النشطون"
            color="success"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <statistic-card-horizontal
            icon="GitBranchIcon"
            :statistic="stats && stats.counts ? stats.counts.direct_children : 0"
            statistic-title="الأقسام الفرعية"
            color="warning"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <statistic-card-horizontal
            icon="LayersIcon"
            :statistic="stats && stats.counts ? stats.counts.total_descendants : 0"
            statistic-title="إجمالي الفروع"
            color="info"
          />
        </b-col>
      </b-row>

      <!-- Content Tabs -->
      <b-card
        no-body
        class="mt-2"
      >
        <b-tabs pills>
          <!-- Overview Tab -->
          <b-tab active>
            <template #title>
              <feather-icon
                icon="InfoIcon"
                size="16"
              />
              <span class="align-middle ml-50">نظرة عامة</span>
            </template>

            <b-card-body>
              <b-row>
                <b-col
                  cols="12"
                  md="6"
                >
                  <h5>معلومات القسم</h5>
                  <dl class="row">
                    <dt class="col-sm-4">
                      الرمز:
                    </dt>
                    <dd class="col-sm-8">
                      <b-badge
                        variant="light-secondary"
                        class="text-monospace"
                      >
                        {{ currentDepartment.code }}
                      </b-badge>
                    </dd>

                    <dt class="col-sm-4">
                      المستوى:
                    </dt>
                    <dd class="col-sm-8">
                      {{ currentDepartment.level }}
                    </dd>

                    <dt class="col-sm-4">
                      القسم الأعلى:
                    </dt>
                    <dd class="col-sm-8">
                      <span v-if="currentDepartment.parent">
                        <b-link :to="{ name: 'departments-view', params: { id: currentDepartment.parent.id } }">
                          {{ currentDepartment.parent.name }}
                        </b-link>
                      </span>
                      <b-badge
                        v-else
                        variant="light-info"
                      >
                        قسم رئيسي
                      </b-badge>
                    </dd>

                    <dt class="col-sm-4">
                      المدير:
                    </dt>
                    <dd class="col-sm-8">
                      <div class="d-flex align-items-center">
                        <div
                          v-if="currentDepartment.manager"
                          class="flex-grow-1"
                        >
                          <strong>{{ currentDepartment.manager.full_name }}</strong>
                          <br>
                          <small class="text-muted">{{ currentDepartment.manager.employee_code }}</small>
                        </div>
                        <b-badge
                          v-else
                          variant="light-warning"
                          class="flex-grow-1"
                        >
                          لا يوجد مدير معين
                        </b-badge>
                        <b-button
                          variant="flat-primary"
                          size="sm"
                          class="ml-2"
                          @click="manageManager"
                        >
                          <feather-icon
                            icon="UserIcon"
                            size="14"
                            class="mr-25"
                          />
                          {{ currentDepartment.manager ? 'تغيير' : 'تعيين' }}
                        </b-button>
                      </div>
                    </dd>

                    <dt class="col-sm-4">
                      تاريخ الإنشاء:
                    </dt>
                    <dd class="col-sm-8">
                      {{ currentDepartment.created_at | moment('YYYY-MM-DD HH:mm') }}
                      <span v-if="currentDepartment.creator">
                        by {{ currentDepartment.creator.name }}
                      </span>
                    </dd>

                    <dt class="col-sm-4">
                      آخر تعديل:
                    </dt>
                    <dd class="col-sm-8">
                      {{ currentDepartment.updated_at | moment('YYYY-MM-DD HH:mm') }}
                      <span v-if="currentDepartment.updater">
                        by {{ currentDepartment.updater.name }}
                      </span>
                    </dd>
                  </dl>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                >
                  <h5>حالة الموظفين</h5>
                  <div
                    v-if="stats && stats.employee_status_breakdown"
                    class="mt-1"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-75">
                      <span>تدريب</span>
                      <b-badge variant="light-warning">
                        {{ stats.employee_status_breakdown.training || 0 }}
                      </b-badge>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-75">
                      <span>متعاقد</span>
                      <b-badge variant="light-success">
                        {{ stats.employee_status_breakdown.contracted || 0 }}
                      </b-badge>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-75">
                      <span>منتهية الخدمة</span>
                      <b-badge variant="light-danger">
                        {{ stats.employee_status_breakdown.terminated || 0 }}
                      </b-badge>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>
          </b-tab>

          <!-- Employees Tab -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="UsersIcon"
                size="16"
              />
              <span class="align-middle ml-50">الموظفين</span>
              <b-badge
                variant="light-primary"
                class="ml-50"
              >
                {{ currentDepartment.employees_count }}
              </b-badge>
            </template>

            <b-card-body>
              <department-employees-list
                v-if="currentDepartment && currentDepartment.id"
                :key="`employees-${currentDepartment.id}`"
                :department-id="currentDepartment.id"
                @error="handleEmployeesError"
              />
              <div
                v-else
                class="text-center py-4"
              >
                <p class="text-muted">
                  جارٍ تحميل معلومات القسم...
                </p>
              </div>
            </b-card-body>
          </b-tab>

          <!-- Sub-Departments Tab -->
          <b-tab
            v-if="currentDepartment.has_children"
            lazy
          >
            <template #title>
              <feather-icon
                icon="GitBranchIcon"
                size="16"
              />
              <span class="align-middle ml-50">الأقسام الفرعية</span>
              <b-badge
                variant="light-warning"
                class="ml-50"
              >
                {{ currentDepartment.children_count }}
              </b-badge>
            </template>

            <b-card-body>
              <department-children-list :department-id="currentDepartment.id" />
            </b-card-body>
          </b-tab>
        </b-tabs>
      </b-card>

      <!-- Activity Logs Section -->
      <b-card
        v-if="hasPermission('view activity_logs')"
        class="mt-2"
      >
        <b-card-header>
          <h5 class="mb-0">
            سجل النشاطات
          </h5>
        </b-card-header>
        <b-card-body>
          <ActivityLogs
            :model-type="'department'"
            :model-id="Number(currentDepartment.id)"
            :show-filters="true"
            :per-page="15"
          />
        </b-card-body>
      </b-card>
    </template>

    <!-- Modals -->
    <department-form-modal
      v-model="showFormModal"
      :department="currentDepartment"
      mode="edit"
      @submitted="handleFormSubmitted"
    />

    <manager-assignment-modal
      v-model="showManagerModal"
      :department="currentDepartment"
      @assigned="handleManagerAssigned"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BBadge,
  BAlert,
  BSpinner,
  BTabs,
  BTab,
  BBreadcrumb,
  BBreadcrumbItem,
  BLink,
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import ActivityLogs from '@/components/ActivityLogs.vue'
import DepartmentFormModal from './components/DepartmentFormModal.vue'
import ManagerAssignmentModal from './components/ManagerAssignmentModal.vue'
import DepartmentEmployeesList from './components/DepartmentEmployeesList.vue'
import DepartmentChildrenList from './components/DepartmentChildrenList.vue'

export default {
  name: 'DepartmentsView',

  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BAlert,
    BSpinner,
    BTabs,
    BTab,
    BBreadcrumb,
    BBreadcrumbItem,
    BLink,
    StatisticCardHorizontal,
    DepartmentFormModal,
    ManagerAssignmentModal,
    DepartmentEmployeesList,
    DepartmentChildrenList,
    ActivityLogs,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      stats: null,
      showFormModal: false,
      showManagerModal: false,
      isLoadingDepartment: false, // Local loading state for this component
    }
  },

  computed: {
    ...mapState('departments', [
      'currentDepartment',
      'departmentAncestors',
      'loading',
      'error',
    ]),
  },

  watch: {
    '$route.params.id': {
      handler(newId, oldId) {
        // Only reload if the route ID actually changed
        if (newId && newId !== oldId) {
          console.log('Route changed from', oldId, 'to', newId)
          this.loadDepartment()
        }
      },
      immediate: false,
    },
  },

  created() {
    this.loadDepartment()
  },

  methods: {
    ...mapActions('departments', [
      'fetchDepartment',
      'fetchDepartmentStats',
      'fetchDepartmentAncestors',
    ]),

    async loadDepartment() {
      const { id } = this.$route.params
      console.log('=== loadDepartment called for ID:', id, 'isLoadingDepartment:', this.isLoadingDepartment, '===')

      // Prevent multiple simultaneous calls using local state
      if (this.isLoadingDepartment) {
        console.log('Already loading department, skipping...')
        return
      }

      this.isLoadingDepartment = true

      try {
        console.log('Fetching department details...')
        await this.fetchDepartment(id)
        console.log('Fetching department ancestors...')
        await this.fetchDepartmentAncestors(id)
        console.log('Fetching department stats...')
        const statsData = await this.fetchDepartmentStats(id)
        this.stats = statsData
        console.log('=== Department loaded successfully ===')
      } catch (error) {
        console.error('=== Error loading department ===', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || 'Failed to load department',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoadingDepartment = false
      }
    },

    editDepartment() {
      this.showFormModal = true
    },

    manageManager() {
      this.showManagerModal = true
    },

    handleFormSubmitted() {
      this.showFormModal = false
      // Only reload the current department data, not everything
      this.reloadCurrentDepartment()
    },

    handleManagerAssigned() {
      this.showManagerModal = false
      // Only reload the current department data, not everything
      this.reloadCurrentDepartment()
    },

    async reloadCurrentDepartment() {
      const { id } = this.$route.params
      try {
        // Only reload the department details, not stats or ancestors
        await this.fetchDepartment(id)
      } catch (error) {
        console.error('Error reloading department:', error)
      }
    },

    handleEmployeesError(error) {
      console.error('Employees tab error:', error)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          icon: 'AlertTriangleIcon',
          text: 'Failed to load employees. Please try again.',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.breadcrumb-slash {
  margin-bottom: 0;
}

dl {
  margin-bottom: 0;
}

dt {
  font-weight: 600;
  color: #6e6b7b;
}

dd {
  margin-bottom: 0.5rem;
}
</style>
